import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { debounce } from 'lodash';
import {
    Box,
    Stack,
    Typography,
    InputAdornment,
    Icon,
    IOption,
    CircularProgress,
    useTheme,
    useAppDispatch,
    useAppSelector,
} from '../../base';
import { emailTemplatesActions, emailTemplatesSelectors } from '../../actions';
import {
    EmailTemplateStatus,
    EmailTemplateType,
    IEmailTemplate,
} from '../../common';
import {
    EmailTemplateCard,
    DeleteTemplateModal,
    TemplateFormModal,
} from '../../components/EmailTemplatesComponents';
import SelectDropdown from '../../components/SelectDropdown';
import {
    StyledPage,
    StyledTopRow,
    StyledSearchField,
    StyledFilterDropdown,
    StyledAddButton,
    StyledGrid,
    StyledFooter,
    StyledEmpty,
    StyledPagination,
} from './EmailTemplatesPage.styles';

const EmailTemplatesPage = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const dispatch = useAppDispatch();

    const templates = useAppSelector(emailTemplatesSelectors.getAllTemplates);
    const currentPage = useAppSelector(emailTemplatesSelectors.getCurrentPage);
    const pageSize = useAppSelector(emailTemplatesSelectors.getPageSize);
    const totalPages = useAppSelector(emailTemplatesSelectors.getTotalPages);
    const totalCount = useAppSelector(emailTemplatesSelectors.getTotalCount);
    const search = useAppSelector(emailTemplatesSelectors.getSearch);
    const filters = useAppSelector(emailTemplatesSelectors.getFilters);
    const isDeletingTemplate = useAppSelector(
        emailTemplatesSelectors.getIsDeletingTemplate
    );
    const isLoadingTemplates = useAppSelector(
        emailTemplatesSelectors.getIsLoadingTemplates
    );

    const [searchInput, setSearchInput] = useState(search);
    const [templateToDelete, setTemplateToDelete] =
        useState<IEmailTemplate | null>(null);

    useEffect(() => {
        dispatch(emailTemplatesActions.fetchTemplates());
    }, [dispatch, currentPage, search, filters]);

    const debouncedSetSearch = useMemo(
        () =>
            debounce((value: string) => {
                dispatch(emailTemplatesActions.setSearch(value));
            }, 300),
        [dispatch]
    );

    useEffect(() => () => debouncedSetSearch.cancel(), [debouncedSetSearch]);

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchInput(event.target.value);
        debouncedSetSearch(event.target.value);
    };

    const openAddTemplate = () => {
        dispatch(emailTemplatesActions.openEditor(null));
    };

    const openEditTemplate = (template: IEmailTemplate) => {
        dispatch(emailTemplatesActions.openEditor(template));
    };

    const handlePageChange = (_event: unknown, page: number) => {
        dispatch(emailTemplatesActions.setCurrentPage(page));
    };

    const handleConfirmDelete = () => {
        if (templateToDelete) {
            dispatch(emailTemplatesActions.deleteTemplate(templateToDelete.id));
        }
        setTemplateToDelete(null);
    };

    const typeValues = Object.values(EmailTemplateType) as string[];
    const statusValues = Object.values(EmailTemplateStatus) as string[];

    const filterOptions: IOption[] = [
        {
            value: EmailTemplateStatus.PUBLISHED,
            label: t('settings.emailTemplates.status.PUBLISHED'),
        },
        {
            value: EmailTemplateStatus.DRAFT,
            label: t('settings.emailTemplates.status.DRAFT'),
        },
        {
            value: EmailTemplateType.AUTOMATED,
            label: t('settings.emailTemplates.type.AUTOMATED'),
        },
        {
            value: EmailTemplateType.MANUAL,
            label: t('settings.emailTemplates.type.MANUAL'),
        },
    ];

    const selectedFilterOptions = filterOptions.filter(
        (option) =>
            option.value === filters.type || option.value === filters.status
    );

    const handleFilterChange = (options: IOption[]) => {
        const values = options.map((option) => option.value);
        const statuses = values.filter((value) =>
            statusValues.includes(value)
        ) as EmailTemplateStatus[];
        const types = values.filter((value) =>
            typeValues.includes(value)
        ) as EmailTemplateType[];
        const status =
            statuses.length <= 1
                ? statuses[0] ?? null
                : statuses.find((value) => value !== filters.status) ??
                  statuses[statuses.length - 1];
        const type =
            types.length <= 1
                ? types[0] ?? null
                : types.find((value) => value !== filters.type) ??
                  types[types.length - 1];
        dispatch(emailTemplatesActions.setFilters({ status, type }));
    };

    const from = totalCount === 0 ? 0 : (currentPage - 1) * pageSize + 1;
    const to = Math.min(currentPage * pageSize, totalCount);

    return (
        <StyledPage>
            <StyledTopRow
                direction="row"
                justifyContent="space-between"
                alignItems="flex-start"
                spacing={2}
                useFlexGap
                flexWrap="wrap"
            >
                <Box>
                    <Typography variant="h4" gutterBottom>
                        {t('settings.emailTemplates.title')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {t('settings.emailTemplates.subtitle')}
                    </Typography>
                </Box>

                <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1.5}
                    useFlexGap
                    flexWrap="wrap"
                >
                    <StyledSearchField
                        size="small"
                        value={searchInput}
                        onChange={handleSearchChange}
                        placeholder={t(
                            'settings.emailTemplates.searchPlaceholder'
                        )}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Icon
                                        name="SearchIcon"
                                        size={18}
                                        color={theme.palette.grey[500]}
                                    />
                                </InputAdornment>
                            ),
                        }}
                    />

                    <StyledFilterDropdown>
                        <SelectDropdown
                            options={filterOptions}
                            selected={null}
                            onChange={() => {}}
                            placeholder={t('settings.emailTemplates.filter')}
                            multiSelect
                            selectedMulti={selectedFilterOptions}
                            onMultiChange={handleFilterChange}
                            sectionAfterValues={[EmailTemplateStatus.DRAFT]}
                        />
                    </StyledFilterDropdown>

                    <StyledAddButton
                        variant="contained"
                        startIcon={<Icon name="AddRoundedIcon" size={20} />}
                        onClick={openAddTemplate}
                    >
                        {t('settings.emailTemplates.addNew')}
                    </StyledAddButton>
                </Stack>
            </StyledTopRow>

            {isLoadingTemplates && templates.length === 0 ? (
                <StyledEmpty>
                    <CircularProgress />
                </StyledEmpty>
            ) : templates.length === 0 ? (
                <StyledEmpty>
                    <Typography variant="body1">
                        {t('settings.emailTemplates.noTemplates')}
                    </Typography>
                </StyledEmpty>
            ) : (
                <StyledGrid>
                    {templates.map((template) => (
                        <EmailTemplateCard
                            key={template.id}
                            template={template}
                            onEdit={openEditTemplate}
                            onDelete={setTemplateToDelete}
                        />
                    ))}
                </StyledGrid>
            )}

            <StyledFooter
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                spacing={2}
                useFlexGap
                flexWrap="wrap"
            >
                <Typography variant="body2" color="text.secondary">
                    {t('settings.emailTemplates.showingCount', {
                        from,
                        to,
                        total: totalCount,
                    })}
                </Typography>
                {totalPages > 1 && (
                    <StyledPagination
                        count={totalPages}
                        page={currentPage}
                        onChange={handlePageChange}
                        shape="rounded"
                    />
                )}
            </StyledFooter>

            <DeleteTemplateModal
                open={Boolean(templateToDelete)}
                onClose={() => setTemplateToDelete(null)}
                onConfirm={handleConfirmDelete}
                isLoading={isDeletingTemplate}
            />

            <TemplateFormModal />
        </StyledPage>
    );
};

export default EmailTemplatesPage;
